import { P2GBox } from '@parcel2go/core-ui';
import { ReactNode } from 'react';
import { useState } from 'react';
import {
  PaymentOptionApiModel,
  PaymentOptionOptionApiModel,
  PaymentOptionType,
  VerifyShipResponse
} from '~/lib/api/smartsend/types';
import PaymentOptionAnalyser from '../../../../lib/helpers/PaymentOptionAnalyser';
import { ProviderGroup, ProviderGroupState } from '../../../../lib/helpers/SmartSendBulkActionApplier';
import Loading from '../../../../../../components/loading/Loading';
import SidePanelSection from '../../../../../../components/sidepanel-section';
import PaymentButton from './PaymentButton';
import PaymentInstructions from './PaymentInstructions';
import ServiceSummary from './ServiceSummary';
import TermsRow from './TermsRow';
import ProviderGroupSummaryContent from './ProviderGroupSummaryContent';
import { PaymentHeader } from './PaymentHeader';
import { P2GGrid } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import useOnLoad from '~/routes/use-on-load';

interface props {
  group: ProviderGroup;
  hidePay: boolean;
  getPaymentOptions: () => PaymentOptionApiModel[];
  updateDiscountCode: (discountCode: string) => void;
  updateSelectedPaymentOption: (option: PaymentOptionType, optionOption: PaymentOptionOptionApiModel) => void;
  updateState: (state: ProviderGroupState) => void;
  book: () => void;
  markAsDispatched: () => void;
  reset: () => void;
  close: () => void;
  removeItems: (ids: string[]) => void;
  visible: boolean;
  isXs: boolean;
  confirmComponent: ReactNode;
  onBooked: () => void;
  totalGroups: number;
  verifyShip: (group: ProviderGroup) => Promise<VerifyShipResponse>;
}

const ProviderGroupSummary: React.FC<props> = (props) => {
  const [loading, setLoading] = useState(true);
  const [paymentOptions, setPaymentOptions] = useState<PaymentOptionApiModel[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useOnLoad(() => {
    const onload = async () => {
      const r = props.getPaymentOptions();
      setPaymentOptions(r);
      const pref = PaymentOptionAnalyser(r, props.group.TotalPrice);
      const op = r.find((x) => x.Type === pref)?.Options || [];
      props.updateSelectedPaymentOption(pref, op[0]);
      setLoading(false);
    };
    onload();
  });

  if (!props.visible) {
    return null;
  }

  if (loading) {
    return <Loading />;
  }

  if (paymentOptions.length === 0) {
    return <P2GBox>{t('NO_PAYMENT_OPTIONS_ERROR', { defaultValue: 'No Payment Options' })}</P2GBox>;
  }

  const po = paymentOptions.find((x) => x.Type === props.group.SelectedPaymentMethod);
  const idleSingleCourier =
    props.group.State == 'IDLE' && props.group.SelectedPaymentMethod == 'CourierAccount' && paymentOptions.length == 1;

  return (
    <SidePanelSection>
      <P2GGrid container direction="column" gap={2}>
        {po && props.totalGroups > 1 && (
          <P2GGrid item>
            <PaymentHeader paymentType={po} />
          </P2GGrid>
        )}
        <P2GGrid item>
          <P2GGrid container direction="column" gap={1}>
            {props.group.Summary.map((s) => (
              <P2GGrid item key={s.Service.Id}>
                <P2GBox mb={1} pl={1} pr={2}>
                  <ServiceSummary item={s} state={props.group.State} />
                </P2GBox>
              </P2GGrid>
            ))}
            {!idleSingleCourier && (
              <P2GGrid item>
                <ProviderGroupSummaryContent
                  verifyShip={props.verifyShip}
                  onBooked={props.onBooked}
                  removeItems={props.removeItems}
                  group={props.group}
                  paymentOptions={paymentOptions}
                  close={props.close}
                  markAsDispatched={props.markAsDispatched}
                  confirmComponent={
                    !props.hidePay &&
                    props.group.State === 'BOOKED' &&
                    props.confirmComponent && <P2GGrid item>{props.confirmComponent}</P2GGrid>
                  }
                  updateSelectedPaymentOption={props.updateSelectedPaymentOption}
                  updateDiscountCode={props.updateDiscountCode}
                />
              </P2GGrid>
            )}
          </P2GGrid>
        </P2GGrid>
        {po && props.group.State == 'IDLE' && (
          <PaymentInstructions
            wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
            close={props.close}
            paymentType={po}
            manifestable={props.group.Manifestable}
            currencyCode={props.group.CurrencyCode}
          />
        )}

        {!props.hidePay && (
          <P2GGrid item>
            <TermsRow
              isXs={props.isXs}
              action={props.group.State === 'IDLE' ? undefined : null}
              termsLink={props.group.Links}
              button={
                <PaymentButton
                  isXs={props.isXs}
                  payLink={props.group.ShipResponse?.Links?.find((x) => x.Type === 'ORDER-PAYMENT_PAGE')?.Href}
                  reset={props.reset}
                  close={props.close}
                  state={props.group.State}
                  provider={props.group.Provider.Provider}
                  onClick={props.book}
                  paymentType={props.group.SelectedPaymentMethod}
                  manifestable={props.group.Manifestable}
                />
              }
            />
          </P2GGrid>
        )}
      </P2GGrid>
    </SidePanelSection>
  );
};

export default ProviderGroupSummary;
